import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { downloadListCsv } from '../utils/csv';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/ProcurementReceiving.jsx (Goods
// Receipts list). `status` is fixed by
// ProcurementFulfillmentController::storeGoodsReceipt() (`in:partially_received,
// fully_received,issue_reported`), not list-item-backed.
const STATUS_LABELS: Record<string, string> = {
  fully_received: 'Fully Received',
  partially_received: 'Partially Received',
  issue_reported: 'Issue Reported',
};

function statusBadgeClass(status: string): string {
  if (status === 'fully_received') return 'badge-normal';
  if (status === 'issue_reported') return 'badge-important';
  return 'badge-neutral';
}

interface GoodsReceiptRow {
  id: number;
  received_at: string;
  condition: string | null;
  status: string;
  purchase_order: { po_number: string; vendor_name: string } | null;
}

export function ProcurementReceiving() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const filters = useMemo(
    () => ({ status: status || undefined, search: search || undefined }),
    [status, search]
  );
  const { data, loading, error, setPage } = usePaginatedList<GoodsReceiptRow>('/goods-receipts', filters);

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<GoodsReceiptRow>(
      api,
      '/goods-receipts',
      { status: status || undefined, search: search || undefined },
      [
        { label: 'PO Number', value: (r) => r.purchase_order?.po_number ?? '' },
        { label: 'Vendor', value: (r) => r.purchase_order?.vendor_name ?? '' },
        { label: 'Received', value: (r) => r.received_at },
        { label: 'Condition', value: (r) => r.condition ?? '' },
        { label: 'Status', value: (r) => STATUS_LABELS[r.status] ?? r.status },
      ],
      'goods-receipts.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Receiving</h1>
        <Link className="btn btn-primary" to="/procurement/receiving/new">
          + Add Receiving
        </Link>
      </header>
      <p className="muted">Goods and services received against issued purchase orders.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search PO number or vendor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>PO Number</th>
                <th>Vendor</th>
                <th>Received</th>
                <th>Condition</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((gr) => (
                  <tr key={gr.id}>
                    <td className="muted">{gr.purchase_order?.po_number}</td>
                    <td>{gr.purchase_order?.vendor_name}</td>
                    <td>{gr.received_at}</td>
                    <td>{gr.condition || '—'}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(gr.status)}`}>
                        {STATUS_LABELS[gr.status] ?? gr.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/procurement/receiving/${gr.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No goods receipts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
